import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { colors, fonts, breakpoints } from './tokens';
import { Button } from './Button';
import { SessionHeader, EngageWordmark } from './Header';

// ---------------------------------------------------------------------------------------------
// 280 mm rule (SPEC 4.7): the patient's screen must be at least 280 mm wide.
// Physical width = screen CSS px x mm-per-CSS-px. When the screen has been calibrated we use the
// measured pixel density; otherwise an estimate by device class (touch devices render ~0.18 mm
// per CSS px, desktop monitors ~0.26).
// ---------------------------------------------------------------------------------------------

export const MIN_SCREEN_WIDTH_MM = 280;
const MM_PER_CSS_PX_TOUCH = 0.18;
const MM_PER_CSS_PX_DESKTOP = 0.2646; // CSS reference pixel (96 dpi)

export const estimateScreenWidthMm = ({ pixelDensity, calibrated, win = typeof window !== 'undefined' ? window : undefined } = {}) => {
  if (!win) return { widthMm: null, source: 'unknown' };
  // Longest side: the patient can rotate the device to use it.
  const cssWidth = Math.max(win.screen?.width || 0, win.screen?.height || 0, win.innerWidth || 0);
  if (calibrated && pixelDensity > 0) {
    // pixelDensity (calibration context) is mm per CSS px, measured with the card/ruler/device.
    return { widthMm: Math.round(cssWidth * pixelDensity), source: 'calibrated' };
  }
  const coarse = win.matchMedia?.('(pointer: coarse)')?.matches;
  return { widthMm: Math.round(cssWidth * (coarse ? MM_PER_CSS_PX_TOUCH : MM_PER_CSS_PX_DESKTOP)), source: 'estimated' };
};

// Phone = touch device whose longest screen side is below a small-tablet's (<= 1000 CSS px) and shortest < 600.
export const isPhoneDevice = (win = typeof window !== 'undefined' ? window : undefined) => {
  if (!win?.screen) return false;
  const long = Math.max(win.screen.width, win.screen.height);
  const short = Math.min(win.screen.width, win.screen.height);
  return Boolean(win.matchMedia?.('(pointer: coarse)')?.matches) && short < 600 && long <= 1000;
};

/**
 * useScreenSizeCheck - { tooSmall, widthMm, source, isPhone, policy }
 * Enforced for the PATIENT role only (the clinician's iPhone/iPad frames are valid Figma targets).
 * Policies (REACT_APP_SCREEN_SIZE_POLICY, default "window"):
 *   window - block when a desktop (fine pointer) browser window is narrower than 280 mm (estimated).
 *            Tablets and phones keep working as today.
 *   strict - also block phones, and any device whose CALIBRATED screen is narrower than 280 mm.
 *            NB: an iPad Mini is ~200 mm wide, so strict blocks the Figma iPad patient frames too.
 *   off    - never block.
 * URL override for testing: ?screencheck=off|force.
 */
export const useScreenSizeCheck = ({ enabled = true, pixelDensity, calibrated = false, policy = process.env.REACT_APP_SCREEN_SIZE_POLICY || 'window' } = {}) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const on = () => setTick((t) => t + 1);
    window.addEventListener('resize', on);
    window.addEventListener('orientationchange', on);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('orientationchange', on);
    };
  }, []);
  const override = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('screencheck') : null;
  const { widthMm, source } = estimateScreenWidthMm({ pixelDensity, calibrated });
  const isPhone = isPhoneDevice();
  const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches;
  let tooSmall = false;
  if (override === 'force') tooSmall = true;
  else if (override === 'off' || !enabled || policy === 'off') tooSmall = false;
  else if (policy === 'strict' && source === 'calibrated') tooSmall = widthMm < MIN_SCREEN_WIDTH_MM;
  else if (policy === 'strict' && isPhone) tooSmall = true;
  else if (!coarse) tooSmall = Math.round((window.innerWidth || 0) * MM_PER_CSS_PX_DESKTOP) < MIN_SCREEN_WIDTH_MM;
  void tick;
  return { tooSmall, widthMm, source, isPhone, policy };
};

// ---------------------------------------------------------------------------------------------
// ScreenTooSmall - "Tiny Screen Alert! Let's Go Full Size". Layout follows the viewport:
//   phone portrait: centred single column, only [Go Back], collage below
//   tablet portrait: title + buttons left, copy right, collage below
//   landscape: copy + buttons left, collage right
// ---------------------------------------------------------------------------------------------

const Root = styled.div`
  position: ${({ overlay }) => (overlay ? 'fixed' : 'relative')};
  inset: 0;
  z-index: 900;
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: #fff;
  font-family: ${fonts.family};
  overflow: auto;
`;

const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  @media (orientation: landscape) and (min-width: 560px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: 1fr;
  }
`;

const Copy = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-areas: 'title text' 'actions text';
  align-content: start;
  column-gap: 32px;
  padding: 48px 40px 40px;
  color: ${colors.textMuted};

  @media (max-width: ${breakpoints.phone - 1}px) and (orientation: portrait) {
    grid-template-columns: 1fr;
    grid-template-areas: 'title' 'text' 'actions';
    text-align: center;
    padding: 32px 24px;
    .try {
      display: none;
    }
    .actions {
      justify-content: center;
    }
  }
  @media (orientation: landscape) {
    grid-template-columns: 1fr;
    grid-template-areas: 'title' 'text' 'actions';
    padding: 32px 32px 24px;
    .actions {
      flex-direction: row;
    }
  }
  h1 {
    grid-area: title;
    margin: 0 0 24px;
    font-size: clamp(28px, 5vw, 44px);
    line-height: 1.15;
    font-weight: 700;
    color: ${colors.slate[600]};
  }
  .text {
    grid-area: text;
    font-size: 15px;
    line-height: 1.75;
  }
  .text p {
    margin: 0 0 18px;
  }
  .text b {
    color: ${colors.slate[600]};
  }
  .actions {
    grid-area: actions;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  @media (max-width: ${breakpoints.phone - 1}px) and (orientation: portrait) {
    .actions {
      align-items: center;
    }
  }
`;

const Collage = styled.div`
  position: relative;
  min-height: 260px;
  overflow: hidden;
  background: linear-gradient(135deg, #5fc3cc 0%, #3aa7b1 45%, #2e8f97 100%);
  .card {
    position: absolute;
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 18px 40px rgba(7, 38, 41, 0.25);
    transform: rotate(-24deg);
  }
  .c1 {
    width: 46%;
    height: 42%;
    left: -4%;
    bottom: 6%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.primary};
  }
  .c2 {
    width: 52%;
    height: 50%;
    right: -8%;
    top: 14%;
    background: #f5fbfb;
  }
  .c2::before {
    content: '';
    position: absolute;
    inset: 16% 10% 18% 10%;
    border-radius: 12px;
    background: linear-gradient(160deg, #2c3e50, #13161b);
  }
  .c3 {
    width: 36%;
    height: 22%;
    left: 30%;
    top: -6%;
    background: #eef7f7;
  }
`;

/**
 * @param onGoBack          [Go Back] handler (default: history.back())
 * @param onTryFullScreen   [Try Going Full Screen] (default: requestFullscreen) - hidden on phones
 * @param overlay           cover the page (position: fixed)
 * @param showHeader        render the session header ("IBISengage Appointment")
 */
export const ScreenTooSmall = ({ onGoBack, onTryFullScreen, overlay = false, showHeader = true, title = 'IBISengage Appointment' }) => {
  const goBack = onGoBack || (() => window.history.back());
  const tryFull =
    onTryFullScreen ||
    (() => {
      const el = document.documentElement;
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
    });
  return (
    <Root overlay={overlay} role='alertdialog' aria-labelledby='tiny-screen-title' data-testid='screen-too-small'>
      {showHeader && <SessionHeader title={title} showLogo />}
      <Body>
        <Copy>
          <h1 id='tiny-screen-title'>
            Tiny Screen Alert!
            <br />
            Let's Go Full Size
          </h1>
          <div className='text'>
            <p>We care about your vision, and to provide you with the best experience, we need a bit more screen space.</p>
            <p>
              <b>Your current device's screen width falls below the minimum requirement of {MIN_SCREEN_WIDTH_MM}mm.</b>
            </p>
            <p>
              To ensure accurate results and a seamless eye care appointment, please switch to a device with a wider screen.{' '}
              <b>If you're using Safari, consider enabling full-screen mode (F11).</b>
            </p>
          </div>
          <div className='actions'>
            <Button className='try' size='lg' onClick={tryFull}>
              Try Going Full Screen
            </Button>
            <Button variant='secondary' size='lg' onClick={goBack}>
              Go Back
            </Button>
          </div>
        </Copy>
        <Collage aria-hidden='true'>
          <div className='card c3' />
          <div className='card c2' />
          <div className='card c1'>
            <EngageWordmark width={140} />
          </div>
        </Collage>
      </Body>
    </Root>
  );
};

export default ScreenTooSmall;
